import {
  Choice, Slider, Counter, SECTION_EMOJI, YES_NO_NSP_BIN,
  buildCommonStartSteps, buildTabacSteps, buildAlcoolSteps, buildMentalSteps, buildRedFlagsStep,
  updateExtra, getExtra,
} from './BilanWidgets';

// Tranche 18-25 ans : focus santé mentale + sexuelle + addictions + vaccinations
export function build1825Steps(config, answers, update) {
  const setEx = (k, v) => updateExtra(answers, update, k, v);
  const gx = (k) => getExtra(answers, k);

  return [
    ...buildCommonStartSteps(config, answers, update),
    ...buildTabacSteps(answers, update),
    ...buildAlcoolSteps(answers, update),
    // Cannabis / substances
    { id: 'cannabis', emoji: '🌿', title: 'Cannabis ou autres substances au cours des 12 derniers mois ?',
      render: (a, u, advance) => <Choice testid="q-cannabis" value={gx('cannabis_use')} onChange={(v) => { setEx('cannabis_use', v); advance(); }}
        options={[{value:'never',label:'Jamais'},{value:'rare',label:'Rarement'},{value:'regular',label:'Régulièrement'}]} />
    },
    // Sommeil
    { id: 'sommeil_heures', emoji: SECTION_EMOJI.sommeil, title: 'Combien d\'heures dormez-vous par nuit en moyenne ?',
      render: () => <Slider testid="q-sleep-h" value={gx('sommeil_heures')} onChange={(v) => setEx('sommeil_heures', v)} min={0} max={14} step={1} unit=" h"
        presets={[{ value: 5, label: '<5h' }, { value: 6, label: '6h' }, { value: 7, label: '7h' }, { value: 8, label: '8h' }, { value: 9, label: '9h' }, { value: 10, label: '10h+' }]} />
    },
    { id: 'sommeil_qualite', emoji: SECTION_EMOJI.sommeil, title: 'Qualité de votre sommeil ?',
      render: (a, u, advance) => <Choice testid="q-sleep-q" value={answers.sommeil_qualite} onChange={(v) => { update({ sommeil_qualite: v }); advance(); }}
        options={[{value:'bon',label:'Bonne'},{value:'moyen',label:'Moyenne'},{value:'mauvais',label:'Mauvaise'}]} />
    },
    // Écrans
    { id: 'ecrans', emoji: '📱', title: 'Heures passées sur les écrans par jour ?', subtitle: "Hors études/travail",
      render: () => <Slider testid="q-screens" value={gx('ecrans_heures')} onChange={(v) => setEx('ecrans_heures', v)} min={0} max={16} step={1} unit=" h/j"
        presets={[
          { value: 1, label: '< 1h' },
          { value: 3, label: '~3h' },
          { value: 6, label: '6h+' },
          { value: 10, label: '10h+' },
        ]} />
    },
    // Activité physique
    { id: 'activite', emoji: SECTION_EMOJI.activite, title: 'Minutes d\'activité physique par semaine ?', subtitle: "Sport, marche rapide, vélo...",
      render: () => <Slider testid="q-act" value={answers.activite_physique_min_sem} onChange={(v) => update({ activite_physique_min_sem: v })}
        min={0} max={600} step={15} unit=" min/sem"
        presets={[
          { value: 0, label: 'Aucune' },
          { value: 60, label: '~1h' },
          { value: 150, label: '~2h30' },
          { value: 300, label: '5h+' },
        ]}
        zones={[
          { color: 'bg-rose-200', label: 'Insuffisant' },
          { color: 'bg-amber-200', label: 'Moyen' },
          { color: 'bg-emerald-200', label: 'Bon' },
        ]} />
    },

    ...buildMentalSteps(answers, update),
    // Stress perçu
    { id: 'stress', emoji: SECTION_EMOJI.mental, title: 'Votre niveau de stress récent ?', subtitle: '0 = aucun · 10 = extrême',
      render: () => <Slider testid="q-stress" value={gx('stress_score')} onChange={(v) => setEx('stress_score', v)} min={0} max={10} step={1} unit="/10"
        presets={[
          { value: 2, label: '😌 Calme' },
          { value: 5, label: '😐 Moyen' },
          { value: 8, label: '😫 Élevé' },
        ]} />
    },

    // Santé sexuelle
    { id: 'sex_active', emoji: '💞', title: 'Avez-vous une activité sexuelle ?',
      render: (a, u, advance) => <Choice testid="q-sex-act" value={gx('activite_sexuelle')}
        onChange={(v) => { setEx('activite_sexuelle', v); advance(); }}
        options={[{value:'yes',label:'Oui'},{value:'no',label:'Non'},{value:'pnr',label:'Préfère ne pas dire'}]} />
    },
    ...(gx('activite_sexuelle') === 'yes' ? [
      { id: 'contraception', emoji: '💊', title: 'Utilisez-vous un moyen de contraception ?',
        render: (a, u, advance) => <Choice testid="q-contra" value={gx('contraception')}
          onChange={(v) => { setEx('contraception', v); advance(); }}
          options={[{value:'yes',label:'Oui'},{value:'no',label:'Non'},{value:'na',label:'Non concerné(e)'}]} />
      },
      { id: 'ist', emoji: '🧪', title: 'Dépistage IST au cours des 12 derniers mois ?',
        render: (a, u, advance) => <Choice testid="q-ist" value={gx('ist_depist')}
          onChange={(v) => { setEx('ist_depist', v); advance(); }}
          options={[{value:'ok',label:'Fait'},{value:'nsp',label:'Pas sûr'},{value:'no',label:'Non'}]} />
      },
    ] : []),
    ...(answers.sex === 'F' && gx('activite_sexuelle') === 'yes' ? [
      { id: 'grossesse', emoji: '🤰', title: 'Grossesse en cours ou récente ?',
        render: (a, u, advance) => <Choice testid="q-grossesse" value={gx('grossesse')}
          onChange={(v) => { setEx('grossesse', v); advance(); }} options={YES_NO_NSP_BIN} />
      },
    ] : []),

    // Vaccinations
    { id: 'vaccin_dtp', emoji: SECTION_EMOJI.vaccin, title: 'Vaccin DTP à jour ?', subtitle: 'Diphtérie — Tétanos — Poliomyélite',
      render: (a, u, advance) => <Choice testid="q-vac-dtp" value={answers.vaccin_dtp} onChange={(v) => { update({ vaccin_dtp: v }); advance(); }}
        options={[{value:'ok',label:'À jour'},{value:'nsp',label:'Pas sûr'},{value:'no',label:'Non'}]} />
    },
    { id: 'vaccin_ror', emoji: SECTION_EMOJI.vaccin, title: 'Rougeole — Oreillons — Rubéole (ROR) à jour ?',
      render: (a, u, advance) => <Choice testid="q-vac-ror" value={gx('vaccin_ror')} onChange={(v) => { setEx('vaccin_ror', v); advance(); }}
        options={[{value:'ok',label:'À jour'},{value:'nsp',label:'Pas sûr'},{value:'no',label:'Non'}]} />
    },
    { id: 'vaccin_hpv', emoji: SECTION_EMOJI.vaccin, title: 'Vaccin HPV (papillomavirus) ?',
      render: (a, u, advance) => <Choice testid="q-vac-hpv" value={gx('vaccin_hpv')} onChange={(v) => { setEx('vaccin_hpv', v); advance(); }}
        options={[{value:'ok',label:'À jour'},{value:'nsp',label:'Pas sûr'},{value:'no',label:'Non'}]} />
    },
    { id: 'vaccin_meningo', emoji: SECTION_EMOJI.vaccin, title: 'Méningocoque ACYW ?',
      render: (a, u, advance) => <Choice testid="q-vac-meningo" value={gx('vaccin_meningo')} onChange={(v) => { setEx('vaccin_meningo', v); advance(); }}
        options={[{value:'ok',label:'À jour'},{value:'nsp',label:'Pas sûr'},{value:'no',label:'Non'}]} />
    },
    { id: 'vaccin_hepb', emoji: SECTION_EMOJI.vaccin, title: 'Hépatite B ?',
      render: (a, u, advance) => <Choice testid="q-vac-hepb" value={gx('vaccin_hepb')} onChange={(v) => { setEx('vaccin_hepb', v); advance(); }}
        options={[{value:'ok',label:'À jour'},{value:'nsp',label:'Pas sûr'},{value:'no',label:'Non'}]} />
    },
    ...buildRedFlagsStep(config, answers, update),
  ];
}
